import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import {
    AuthenticatedMessageBox,
    useCurrentDossierCtx,
    useDocumentPreviewContext,
    useDossierDocumentFilesApi,
    useOfferContext,
} from '@europrocurement/l2d-modules';
import {
    DocumentItemDetailData,
    IconItemDetailData,
    MessageItem,
    MessageItemDetailData,
    TopicType,
    useWebsocketActions,
    useWebsocketState,
    WebsocketPayloadDeleteMessage,
    WebsocketPayloadEditMessage,
    WebsocketPayloadMessage,
} from '@europrocurement/l2d-websocket';
import React, { useEffect, useMemo, useState } from 'react';
import {
    ApiFichiersPostExpediteurEnum,
    ApiFichiersPostExpeditionEnum,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { enqueueSnackbar } from 'notistack';
import { BandeauContact } from '@europrocurement/flexy-components/components/organisms/MessageBox';
import {
    CenterCircularProgress,
    ListCardBone,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';

export const MessageTab: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const [currentOfferLoaded, setCurrentOfferLoaded] = useState<boolean>(false);
    const { offerDataLoaded, getTiersCurrentOfferWithDefault } = useOfferContext();
    const [utilisateurPremium, setUtilisateurPremium] = useState<boolean>(false);
    const topicId = useMemo(
        () => (dossier?.id ? `messagerie_dossier_formalite_${dossier.id}` : null),
        [dossier?.id],
    );

    const keycloakService = UseKeycloakService();
    const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;
    const keycloakInstance = keycloakService.getKc();
    const userInfo = keycloakService.getTokenParsed();
    const isInterne = userInfo?.realm_access?.roles.includes('interne');

    const { messagesByTopic, socketOpen, isConnectedByUserIdentifier } = useWebsocketState();
    const {
        sendMessage,
        updateMessage,
        deleteMessage,
        fetchMessages,
        getIsConnected,
        setMessagesRead,
    } = useWebsocketActions();

    const expediteur = isInterne
        ? ApiFichiersPostExpediteurEnum.NUMBER_3
        : ApiFichiersPostExpediteurEnum.NUMBER_1;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError] = useState<boolean>(false);
    const [messages, setMessages] = useState<MessageItem[]>([]);
    const isFormaliste: boolean =
        keycloakService.getTokenParsed()?.realm_access?.roles.includes('interne') ?? false;
    const [isFormalisteConnecte, setIsFormalisteConnecte] = useState<boolean>(false);
    const { setPreviewLoading, setDocumentPreview } = useDocumentPreviewContext();
    const { getDosDocFileContent, uploadAdditionnalDocDosFile } = useDossierDocumentFilesApi();
    const topicType: TopicType = 'messagerie_dossier';

    useEffect(() => {
        if (dossier && dossier.formalisteUserIdentifier) {
            setIsFormalisteConnecte(isConnectedByUserIdentifier[dossier.formalisteUserIdentifier]);
        }
    }, [isConnectedByUserIdentifier, dossier, dossier?.formalisteUserIdentifier]);

    useEffect(() => {
        if (!dossier) {
            return;
        }
        if (!topicId) {
            return;
        }
        if (socketOpen) {
            if (isFormaliste && dossier?.contactUserIdentifier)
                getIsConnected(dossier?.contactUserIdentifier);
            else if (dossier.formalisteUserIdentifier)
                getIsConnected(dossier.formalisteUserIdentifier);
            fetchMessages(topicId, topicType);
        }
    }, [
        socketOpen,
        topicId,
        dossier,
        dossier?.formalisteUserIdentifier,
        dossier?.contactUserIdentifier,
        fetchMessages,
        isFormaliste,
        getIsConnected,
    ]);

    useEffect(() => {
        if (!topicId) {
            return;
        }
        setIsLoading(false);
        if (messagesByTopic[topicId]) {
            const messagesOnTopic = messagesByTopic[topicId];
            setMessages(messagesOnTopic);
            const messagesIds = messagesOnTopic
                .filter((message) =>
                    isFormaliste
                        ? message.data.reader.interne === 'false'
                        : message.data.reader.client === 'false',
                )
                .map((message) => message.messageId);
            setMessagesRead({ messagesIds, topicType, topicId });
        }
    }, [messagesByTopic, topicId, isFormaliste, setMessagesRead]);

    useEffect(() => {
        if (!offerDataLoaded || !dossier) {
            return;
        }
        (async () => {
            const offer = await getTiersCurrentOfferWithDefault(dossier.prescripteurId as number);
            setCurrentOfferLoaded(true);
            setUtilisateurPremium(offer?.type === 'FULL');
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.prescripteurId, offerDataLoaded]);

    if (
        !dossier?.id ||
        !userIdentifier ||
        !topicId ||
        !dossier.prescripteurId ||
        !currentOfferLoaded
    )
        return <CenterCircularProgress />;

    const sendWebSocketMessage = async (
        messageSend: MessageItemDetailData | DocumentItemDetailData | IconItemDetailData,
    ) => {
        if (!socketOpen) {
            enqueueSnackbar('La connexion est fermée, veuillez réessayer', { variant: 'error' });
            return;
        }

        const token = await keycloakInstance
            .updateToken(10)
            .then(() => keycloakInstance.token || '');

        if (token === undefined) {
            enqueueSnackbar('Vous devez être connecté pour envoyer un message', {
                variant: 'error',
            });
            return;
        }
        const payload: WebsocketPayloadMessage = {
            payload: {
                token,
                topicType: 'messagerie_dossier',
                topicId,
                userIdentifier: keycloakService.getTokenParsed()?.preferred_username,
                date: new Date().toISOString(),
                discriminant: {
                    idPrescripteur: dossier.prescripteurId ?? 0,
                    idDossierFormalite: dossier.id ?? 0,
                    userIdentifierFormaliste: '',
                    idFormaliste: dossier.formalisteId ?? 0,
                    idFormalite: dossier.formaliteId ?? 0,
                    idClient: dossier.clientId ?? 0,
                },
                message: messageSend,
            },
        };

        sendMessage(payload);
    };

    const updateWebSocketMessage = async (
        messageId: string,
        messageSend: MessageItemDetailData,
    ) => {
        const token = await keycloakInstance
            .updateToken(10)
            .then(() => keycloakInstance.token || '');

        if (token === undefined) {
            enqueueSnackbar('Vous devez être connecté pour modifier un message', {
                variant: 'error',
            });
            return;
        }
        const payload: WebsocketPayloadEditMessage = {
            payload: {
                messageId,
                token,
                message: messageSend.message,
            },
        };
        updateMessage(payload);
    };

    const deleteWebSocketMessage = async (messageId: string) => {
        const token = await keycloakInstance
            .updateToken(10)
            .then(() => keycloakInstance.token || '');

        if (token === undefined) {
            enqueueSnackbar('Vous devez être connecté pour supprimer un message', {
                variant: 'error',
            });
            return;
        }
        const payload: WebsocketPayloadDeleteMessage = {
            payload: {
                messageId,
                token,
            },
        };
        deleteMessage(payload);
    };

    const handleUploadFile = async (file: File) => {
        try {
            if (!dossier.id) {
                enqueueSnackbar('Dossier non trouvé', { variant: 'error' });
                return;
            }
            const response = await uploadAdditionnalDocDosFile(
                dossier.id,
                file,
                expediteur,
                ApiFichiersPostExpeditionEnum.NUMBER_5,
                false,
            );

            if (!response) throw new Error('Upload failed');
            if (response.files) {
                response.files.forEach((fichier) => {
                    if (!fichier.id || !fichier.fichier || !fichier.contentUrl) {
                        enqueueSnackbar('Réponse du serveur invalide', { variant: 'error' });
                        return;
                    }
                    const dosDocFileId = fichier.id;
                    const dosDocFileName = fichier.fichier;
                    const dosDocFileUrl = fichier.contentUrl;
                    sendWebSocketMessage({
                        type: 'document',
                        dosDocFileId,
                        dosDocFileName,
                        dosDocFileUrl,
                    } as DocumentItemDetailData);
                    enqueueSnackbar('Votre fichier a bien été ajouté');
                });
            }
        } catch (err) {
            enqueueSnackbar(`Une erreur est survenue "${err}"`, { variant: 'error' });
        }
    };

    const handleDocPreview = async (fileName: string, id: number) => {
        setPreviewLoading(true);
        try {
            const res = await getDosDocFileContent(id);

            setDocumentPreview({
                fileName,
                fileType: 'pdf',
                content: res.data as unknown as Blob,
            });
            setPreviewLoading(false);
        } catch (err) {
            console.log(err);
            enqueueSnackbar(`Une erreur est survenue lors de l'affichage du document ${id}`, {
                variant: 'error',
            });
        }
    };

    const getDestinataire = (
        innerDossier: DossierOpenSearch,
        isDestinataireFormaliste: boolean,
    ): BandeauContact => {
        if (!innerDossier) return { userIdentifier: '', role: '' };
        if (isDestinataireFormaliste) {
            const badge =
                dossier?.contactUserIdentifier &&
                isConnectedByUserIdentifier[dossier?.contactUserIdentifier]
                    ? '#4caf50'
                    : '#9E9E9E'; // Vert si connecté
            return {
                userIdentifier: dossier?.contactUserIdentifier ?? '',
                role: utilisateurPremium ? 'Client Premium' : 'Client Essentiel',
                // mail: dossier.contactMail ?? undefined,
                // phone: dossier.contactTel ?? undefined,
                badge,
            };
        }
        const role = {
            role: utilisateurPremium ? 'Votre formaliste attitré(e)' : '',
        };
        const badge = isFormalisteConnecte ? '#4caf50' : '#9E9E9E'; // Vert si connecté
        return {
            userIdentifier: dossier.formalisteUserIdentifier ?? '',
            // mail: dossier.formalisteMail ?? undefined,
            // phone: dossier.formalisteNumero ?? undefined,
            badge,
            ...role,
        };
    };

    const handleScrollTop = () => {};

    const destinataire: BandeauContact = getDestinataire(dossier, isFormaliste);
    const isLoadingLoader = !(topicId && dossier);

    return (
        <SkeletonLoader
            isLoading={isLoadingLoader}
            skeleton={
                <ListCardBone
                    itemCount={5}
                    contentHeight="220px"
                    gap="16px"
                />
            }
        >
            <AuthenticatedMessageBox
                destinataire={destinataire}
                messages={messages}
                onSend={sendWebSocketMessage}
                onEdit={updateWebSocketMessage}
                onDelete={deleteWebSocketMessage}
                onUploadFile={handleUploadFile}
                userIdentifier={userIdentifier}
                onScrollTop={handleScrollTop}
                onMessageDocumentClick={handleDocPreview}
                isLoading={isLoading}
                isError={isError}
                anonymous={!utilisateurPremium}
                isFormaliste={isFormaliste}
            />
        </SkeletonLoader>
    );
};
